<template>
  <div class="offcanvas-body">
    <template>
      <div v-for="rating in ratings" :key="rating.id">
        <RatingItem
          :rating="rating"
          :submitting="submittingIds.has(rating.id)"
          @handleRemove="(id: number) => onRemove(id)"
          @handleChange="(item: Rating) => handleChange(item)"
        />
      </div>
    </template>
    <ModalComponent
      title="Deseja reportar esta avaliação?"
      id="report_rating_modal"
      backdrop="false"
    >
      <template v-slot:body>
        <p class="text-paragraph">
          Esta avaliação será analisada, e se for inadequada será removida.
        </p>
        <TextAreaInput
          class="mb-0"
          :model="reasonReportModel"
          placeholder="Qual motivo deseja reportar?"
          :maxLength="200"
        />
      </template>
      <template v-slot:footer>
        <FormButton
          color="danger"
          fullWidth
          :loading="submitting"
          @onClick="handleConfirmReport"
          label="Reportar"
          :disabled="!meta.valid"
        />
      </template>
    </ModalComponent>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import type { Rating } from "@phurshell/psiapp-frontend-core/domain";
import { toTypedSchema } from "@vee-validate/zod";
import { useForm } from "vee-validate";
import { useVeeField } from "@/composables/useVeeField";
import { reportRatingSchema } from "@/schemas/profile";
import { EventBus, EventBusNames } from "@/utils/EventBus";
import { toggleModal } from "@/utils/Helper";
import { syncSession } from "@/di/useCases";
import {
  useRatings,
  useRemoveRating,
  useUpdateRating,
} from "@/composables/useRatings";
import AppointmentService from "@/app/services/AppointmentService";
import RatingItem from "./RatingItem.vue";

const { ratings, refetch: refetchRatings } = useRatings();
const { mutateAsync: removeRating } = useRemoveRating();
const { mutateAsync: updateRating } = useUpdateRating();

const submittingIds = ref(new Set<number>());
const currentRating = ref<Rating | null>(null);

const {
  handleSubmit,
  meta,
  resetForm: resetReportForm,
} = useForm({
  validationSchema: toTypedSchema(reportRatingSchema),
  initialValues: { reason_report: "" },
});

const reasonReportModel = useVeeField("reason_report");

function handleReport(rating: Rating) {
  currentRating.value = rating;
  toggleModal("report_rating_modal");
}

const submitting = ref(false);

const handleConfirmReport = handleSubmit(async (values) => {
  try {
    submitting.value = true;
    const params = {
      appointment_id: (currentRating.value as any)?.appointment?.id,
      called_rating_id: currentRating.value?.id,
      reason_report: values.reason_report,
    };
    await new AppointmentService().reviewCallRatings(params);
    EventBus.$emit(EventBusNames.SHOW_TOAST, {
      title: "Avaliação em análise",
      msg: "Nosso time irá analisar o conteúdo da sua avaliação.",
    });
    await refetchRatings();
    resetForm();
  } catch (error) {
    console.error(error);
  } finally {
    submitting.value = false;
  }
});

function resetForm() {
  resetReportForm({ values: { reason_report: "" } });
  toggleModal("report_rating_modal");
}

function onRemove(id: number) {
  EventBus.$emit(EventBusNames.SHOW_CONFIRMATION_ALERT, {
    title: "Deseja remover este depoimento?",
    message:
      "Uma vez que um depoimento for removido, não será possível recupera-lo.",
    confirmationButtonLabel: "Sim, remover",
    cancellationButtonLabel: "Não, voltar",
    confirmCallback: () => handleRemove(id),
  });
}

async function handleRemove(id: number) {
  await removeRating(id);
  EventBus.$emit(EventBusNames.SHOW_TOAST, {
    title: "Depoimento removido",
    msg: "O depoimento foi removido e não está mais visível.",
  });
  await syncSession.execute();
}

async function handleChange(rating: Rating) {
  submittingIds.value = new Set([...submittingIds.value, rating.id]);
  try {
    await updateRating({ id: rating.id, active: !rating.active });
    await syncSession.execute();
  } finally {
    const next = new Set(submittingIds.value);
    next.delete(rating.id);
    submittingIds.value = next;
  }
}
</script>
